import axios from 'axios';
import config from '../config';
import { supabase } from './supabase';
import { FinancialData, PricePoint, CapeScore, FScore, PnFChart, TradingSignal } from '../types';
import cacheService from './cacheService';

// API constants
const API_KEY = config.apiKey;
const BASE_URL = 'https://www.alphavantage.co/query';
const CACHE_KEY_PREFIX = 'alphavantage_';

// Top S&P 500 tickers by market cap (fallback)
const TOP_SP500_TICKERS = [
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'GOOG', 'NVDA', 'BRK.B', 'TSLA',
  'UNH', 'JNJ', 'JPM', 'V', 'PG', 'XOM', 'HD', 'CVX', 'MA', 'AVGO', 'ABBV'
];

// Rate limiting implementation
const API_CALLS = {
  count: 0,
  resetTime: Date.now() + 60000, // Reset after 1 minute
};

// Check if we can make an API call now, or need to wait
const canMakeApiCall = (): boolean => {
  const now = Date.now();
  
  // Reset counter if it's time
  if (now > API_CALLS.resetTime) {
    API_CALLS.count = 0;
    API_CALLS.resetTime = now + 60000; // Reset after 1 minute
    return true;
  }
  
  // Alpha Vantage free tier: 5 calls per minute
  return API_CALLS.count < 5;
};

// Queue for API requests
const requestQueue: (() => Promise<any>)[] = [];
let isProcessingQueue = false;

// Process the queue one request at a time with rate limiting
const processQueue = async () => {
  if (isProcessingQueue || requestQueue.length === 0) return;
  
  isProcessingQueue = true;
  try {
    // Wait until we can make a request
    while (!canMakeApiCall()) {
      // Calculate time to wait until next minute starts
      const timeToWait = API_CALLS.resetTime - Date.now();
      console.log(`Rate limit reached, waiting ${timeToWait}ms for next API call...`);
      await new Promise(resolve => setTimeout(resolve, timeToWait + 100)); // Add 100ms buffer
    }
    
    // Make the request
    const request = requestQueue.shift();
    if (request) {
      API_CALLS.count++;
      await request();
    }
  } finally {
    isProcessingQueue = false;
    // Continue processing the queue if there are more requests
    if (requestQueue.length > 0) {
      setTimeout(processQueue, 100); // Small delay between consecutive operations
    }
  }
};

// Add a request to the queue
const enqueueRequest = <T>(request: () => Promise<T>): Promise<T> => {
  return new Promise((resolve, reject) => {
    requestQueue.push(async () => {
      try {
        const result = await request();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
    
    // Start processing the queue if it's not already running
    if (!isProcessingQueue) {
      processQueue();
    }
  });
};

/**
 * Fetch data from Alpha Vantage API with rate limiting and caching
 */
export const fetchAlphaVantageData = async (
  functionName: string, 
  symbol: string, 
  params: Record<string, string> = {},
  forceRefresh: boolean = false
): Promise<any> => {
  const cacheKey = `${CACHE_KEY_PREFIX}${functionName}_${symbol}_${JSON.stringify(params)}`;
  
  // Use the enhanced cache service
  return cacheService.getCachedData(
    cacheKey,
    () => enqueueRequest(async () => {
      console.log(`Fetching ${functionName} for ${symbol} from Alpha Vantage...`);
      
      const response = await axios.get(BASE_URL, {
        params: {
          function: functionName,
          symbol,
          apikey: API_KEY,
          ...params
        }
      });
      
      // Check for rate limit messages
      if (response.data?.Note && response.data.Note.includes('API call frequency')) {
        console.error('Alpha Vantage rate limit reached:', response.data.Note);
        throw new Error('API rate limit reached. Please try again in a minute.');
      }
      
      // Also store in Supabase for longer-term caching if we have access
      try {
        if (supabase) {
          await supabase.from('financial_data_cache').upsert({
            key: cacheKey,
            data: response.data,
            ticker: symbol,
            function_name: functionName,
            created_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + cacheService.LONG_TTL).toISOString()
          });
        }
      } catch (error) {
        console.error('Failed to store data in Supabase:', error);
        // Continue even if Supabase fails - local caching will still work
      }
      
      return response.data;
    }),
    {
      ttl: cacheService.LONG_TTL, // Use long TTL for financial data
      useLocalStorage: true,
      forceRefresh
    }
  );
};

/**
 * Get S&P 500 tickers
 * Note: Alpha Vantage doesn't provide a direct API for this, so we use
 * a combination of techniques
 */
export const getSP500Tickers = async (): Promise<string[]> => {
  console.log('Getting S&P 500 tickers...');
  
  // Try to get from cache first
  const cacheKey = `${CACHE_KEY_PREFIX}sp500_tickers`;
  
  return cacheService.getCachedData(
    cacheKey,
    async () => {
      try {
        // Try to get from Supabase first if available
        if (supabase) {
          const { data: tickersData, error } = await supabase
            .from('sp500_tickers')
            .select('ticker')
            .order('ticker');
            
          if (!error && tickersData && tickersData.length > 0) {
            console.log(`Found ${tickersData.length} S&P 500 tickers in Supabase`);
            return tickersData.map(t => t.ticker);
          }
        }
        
        // If no data in Supabase, try to get S&P 500 data from Alpha Vantage
        // We'll use LISTING_STATUS endpoint to get all US stocks, then filter for S&P 500
        // This is a workaround since Alpha Vantage doesn't have a direct S&P 500 endpoint
        const listingData = await fetchAlphaVantageData('LISTING_STATUS', '', { state: 'active' });
        
        if (listingData && listingData.data) {
          // Try to identify likely S&P 500 tickers (large cap, US stocks)
          // This is an approximation - in a real app, we'd use a more reliable source
          const likelyTickers = listingData.data
            .filter((item: any) => 
              item.exchange === 'NYSE' || item.exchange === 'NASDAQ')
            .map((item: any) => item.symbol)
            .slice(0, 505); // Take a few extra to account for any errors
            
          console.log(`Identified ${likelyTickers.length} potential S&P 500 tickers`);
          
          // If available, store these in Supabase for future use
          if (supabase && likelyTickers.length > 0) {
            const tickersForDb = likelyTickers.map((ticker: string) => ({
              ticker,
              added_date: new Date().toISOString()
            }));
            
            await supabase.from('sp500_tickers').upsert(tickersForDb);
          }
          
          return likelyTickers;
        }
      } catch (error) {
        console.error('Error fetching S&P 500 tickers:', error);
      }
      
      // Fallback to hard-coded list if all else fails
      console.log('Using hardcoded top S&P 500 tickers as fallback');
      return TOP_SP500_TICKERS;
    },
    {
      ttl: 7 * 24 * 60 * 60 * 1000, // Cache for one week
      useLocalStorage: true
    }
  );
};

/**
 * Get daily price history for a ticker
 */
export const getDailyPrices = async (symbol: string, full = false): Promise<PricePoint[]> => {
  try {
    const outputSize = full ? 'full' : 'compact';
    const data = await fetchAlphaVantageData('TIME_SERIES_DAILY', symbol, { outputSize });
    
    // Parse the time series data
    const timeSeries = data['Time Series (Daily)'];
    if (!timeSeries) {
      console.error('No time series data returned for', symbol);
      return [];
    }
    
    // Convert to PricePoint array
    const pricePoints: PricePoint[] = Object.entries(timeSeries).map(([date, values]: [string, any]) => ({
      date,
      open: parseFloat(values['1. open']),
      high: parseFloat(values['2. high']),
      low: parseFloat(values['3. low']),
      close: parseFloat(values['4. close']),
      volume: parseInt(values['5. volume'], 10)
    }));
    
    // Sort by date, newest first
    return pricePoints.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  } catch (error) {
    console.error('Error getting daily prices:', error);
    return [];
  }
};

/**
 * Get income statement data for a ticker
 */
export const getIncomeStatement = async (symbol: string): Promise<any[]> => {
  try {
    const data = await fetchAlphaVantageData('INCOME_STATEMENT', symbol);
    return data.quarterlyReports || [];
  } catch (error) {
    console.error('Error getting income statement for', symbol, error);
    return [];
  }
};

/**
 * Get balance sheet data for a ticker
 */
export const getBalanceSheet = async (symbol: string): Promise<any[]> => {
  try {
    const data = await fetchAlphaVantageData('BALANCE_SHEET', symbol);
    return data.quarterlyReports || [];
  } catch (error) {
    console.error('Error getting balance sheet for', symbol, error);
    return [];
  }
};

/**
 * Get cash flow data for a ticker
 */
export const getCashFlow = async (symbol: string): Promise<any[]> => {
  try {
    const data = await fetchAlphaVantageData('CASH_FLOW', symbol);
    return data.quarterlyReports || [];
  } catch (error) {
    console.error('Error getting cash flow for', symbol, error);
    return [];
  }
};

/**
 * Get overview data for a ticker
 */
export const getCompanyOverview = async (symbol: string): Promise<any> => {
  try {
    return await fetchAlphaVantageData('OVERVIEW', symbol);
  } catch (error) {
    console.error('Error getting company overview for', symbol, error);
    return {};
  }
};

/**
 * Get earnings data for a ticker
 */
export const getEarnings = async (symbol: string): Promise<any> => {
  try {
    return await fetchAlphaVantageData('EARNINGS', symbol);
  } catch (error) {
    console.error('Error getting earnings data for', symbol, error);
    return { quarterlyEarnings: [] };
  }
};

/**
 * Get complete financial data for a ticker
 */
export const getFinancialData = async (symbol: string): Promise<FinancialData> => {
  try {
    // Create a unique cache key for this combined data
    const cacheKey = `${CACHE_KEY_PREFIX}financial_data_${symbol}`;
    
    return cacheService.getCachedData(
      cacheKey,
      async () => {
        // Fetch all required data in parallel for efficiency
        const [priceHistory, incomeStatement, balanceSheet, cashFlow] = await Promise.all([
          getDailyPrices(symbol),
          getIncomeStatement(symbol),
          getBalanceSheet(symbol),
          getCashFlow(symbol)
        ]);
        
        return {
          ticker: symbol,
          priceHistory,
          incomeStatement,
          balanceSheet,
          cashFlow
        };
      },
      {
        ttl: cacheService.LONG_TTL, // Cache for a long time (24 hours)
        useLocalStorage: true
      }
    );
  } catch (error) {
    console.error('Error getting financial data for', symbol, error);
    throw error;
  }
};

/**
 * Generate mock price data for testing
 */
const mockCurrentPrice = (symbol: string): number => {
  // Use a hash of the symbol to generate a deterministic but realistic stock price
  const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // Calculate a realistic base price based on the hash
  let basePrice = (hash % 1000) + 20; // Range of $20 to $1019
  
  // Adjust price for well-known high-value stocks
  if (symbol === 'AMZN') basePrice = 128.95;
  if (symbol === 'AAPL') basePrice = 188.28;
  if (symbol === 'MSFT') basePrice = 318.34;
  if (symbol === 'GOOGL') basePrice = 137.14;
  if (symbol === 'META') basePrice = 297.74;
  if (symbol === 'NVDA') basePrice = 816.13;
  if (symbol === 'TSLA') basePrice = 238.45;
  
  // Add some randomness based on current time for small variations
  const now = new Date();
  const minuteVariation = (now.getMinutes() % 10) / 1000;
  const secondVariation = (now.getSeconds() % 30) / 500;
  const randomFactor = Math.random() * 0.02 - 0.01; // -1% to +1%
  
  return basePrice * (1 + minuteVariation + secondVariation + randomFactor);
};

/**
 * Fetch current price for a ticker
 */
export const getCurrentPrice = async (symbol: string): Promise<number> => {
  // Return mock prices when using mock API
  if (config.useMockApi) {
    console.log(`Using mock price for ${symbol}`);
    await new Promise(resolve => setTimeout(resolve, 200)); // Simulate API delay
    return mockCurrentPrice(symbol);
  }
  
  try {
    const priceData = await fetchAlphaVantageData('GLOBAL_QUOTE', symbol);
    if (priceData['Global Quote'] && priceData['Global Quote']['05. price']) {
      return parseFloat(priceData['Global Quote']['05. price']);
    }
    throw new Error('Price data not found');
  } catch (error) {
    // Try to get from daily prices as fallback
    try {
      const dailyPrices = await getDailyPrices(symbol, false);
      if (dailyPrices.length > 0) {
        return dailyPrices[0].close;
      }
    } catch {
      console.error('Failed to get price from daily data as well');
    }
    
    console.error('Error getting current price for', symbol, error);
    throw new Error(`Could not get current price for ${symbol}`);
  }
};

/**
 * Fetch current prices for multiple tickers
 */
export const getBatchPrices = async (symbols: string[]): Promise<Record<string, number>> => {
  // Return mock prices when using mock API
  if (config.useMockApi) {
    console.log(`Using mock prices for ${symbols.length} symbols`);
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
    
    const result: Record<string, number> = {};
    for (const symbol of symbols) {
      result[symbol] = mockCurrentPrice(symbol);
    }
    return result;
  }
  
  const result: Record<string, number> = {};
  
  // Process in batches of 5 to respect rate limits
  const batchSize = 5;
  
  for (let i = 0; i < symbols.length; i += batchSize) {
    const batch = symbols.slice(i, i + batchSize);
    const promises = batch.map(async (symbol) => {
      try {
        result[symbol] = await getCurrentPrice(symbol);
      } catch (error) {
        console.error(`Error fetching price for ${symbol}:`, error);
        // Use a default value or last known price
        result[symbol] = 0;
      }
    });
    
    await Promise.all(promises);
    
    // Add a delay before the next batch if there are more batches
    if (i + batchSize < symbols.length) {
      await new Promise(resolve => setTimeout(resolve, 65000)); // 65s to be safe with rate limits
    }
  }
  
  return result;
};

/**
 * Get complete stock analysis for a ticker
 * This combines financial data, CAPE score, F-Score, and P&F chart
 */
export const getStockAnalysis = async (symbol: string) => {
  try {
    // Create a unique cache key for this combined analysis
    const cacheKey = `${CACHE_KEY_PREFIX}stock_analysis_${symbol}`;
    
    return cacheService.getCachedData(
      cacheKey,
      async () => {
        const financialData = await getFinancialData(symbol);
        
        // For now, we'll use placeholder functions for these components
        // In a real implementation, these would calculate the actual values
        // based on financial data
        const capeScore: CapeScore = {
          ticker: symbol,
          cape: 25,
          capePercentile: 50,
          date: new Date().toISOString().split('T')[0]
        };
        
        const fScore: FScore = {
          ticker: symbol,
          score: 5,
          roa: true,
          cfo: true,
          deltaRoa: false,
          accrual: true,
          deltaLeverage: false,
          deltaLiquidity: false,
          noEquityRaise: true,
          deltaMargin: false,
          deltaTurnover: true
        };
        
        const pnfChart: PnFChart = {
          ticker: symbol,
          columns: [
            { direction: 'up', boxes: [100, 101, 102], high: 102, low: 100 }
          ],
          currentPrice: financialData.priceHistory[0]?.close || 100,
          patterns: {
            tripleTopBreakout: false,
            tripleTopBreakdown: false,
            quadTopBreakout: false,
            quadTopBreakdown: false
          }
        };
        
        return {
          financialData,
          capeScore,
          fScore,
          pnfChart
        };
      },
      {
        ttl: cacheService.LONG_TTL / 2, // Cache for 12 hours
        useLocalStorage: true
      }
    );
  } catch (error) {
    console.error('Error getting stock analysis for', symbol, error);
    throw error;
  }
};

// More functions for Trade Strategy implementation will be added in future commits

export default {
  getSP500Tickers,
  getDailyPrices,
  getIncomeStatement,
  getBalanceSheet,
  getCashFlow,
  getCompanyOverview,
  getEarnings,
  getFinancialData,
  getCurrentPrice,
  getBatchPrices,
  getStockAnalysis
}; 